import { useState } from 'react';
import { defaultFilter } from 'cmdk';
import { Check, ChevronsUpDown, UserRound } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { useEnteringFor } from '@/contexts/EnteringForContext';
import { useTimesheet } from '@/contexts/TimesheetContext';

/** Items are keyed by id; search matches their display names (passed as keywords). */
function filterByName(_value: string, search: string, keywords?: string[]) {
  return defaultFilter(keywords?.join(' ') ?? '', search);
}

/**
 * "Entering for:" searchable dropdown shown in the timesheet header.
 * Only rendered when the organisation has "Enter time for others" enabled in
 * Settings → General → On Behalf Of — the parent is responsible for the gate.
 */
export function EnteringForSwitcher() {
  const [open, setOpen] = useState(false);
  const { permittedPeople, selectedPersonId, setSelectedPersonId } = useEnteringFor();
  const { currentEmployee } = useTimesheet();

  const currentUserName = currentEmployee?.name ?? 'Me';
  const myselfLabel = `Myself (${currentUserName})`;

  /** The label shown on the trigger button. */
  const triggerLabel =
    selectedPersonId === null
      ? myselfLabel
      : (permittedPeople.find(p => p.id === selectedPersonId)?.name ?? myselfLabel);

  function handleSelect(personId: string | null) {
    setSelectedPersonId(personId);
    setOpen(false);
  }

  return (
    <div className="flex items-center gap-2 flex-shrink-0">
      <span className="text-sm font-medium text-muted-foreground whitespace-nowrap">
        Entering for:
      </span>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-label="Select person to enter time for"
            className="h-9 min-w-[160px] max-w-[220px] justify-between gap-1 px-3 text-sm font-normal"
          >
            <span className="flex items-center gap-1.5 truncate">
              <UserRound className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
              <span className="truncate">{triggerLabel}</span>
            </span>
            <ChevronsUpDown className="h-3.5 w-3.5 opacity-50 flex-shrink-0" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[240px] p-0" align="end">
          <Command filter={filterByName}>
            <CommandInput placeholder="Search person..." className="h-9" />
            <CommandList>
              <CommandEmpty>No person found.</CommandEmpty>
              <CommandGroup>
                {/* "Myself" option always first */}
                <CommandItem
                  value="myself"
                  keywords={[myselfLabel]}
                  onSelect={() => handleSelect(null)}
                  className="gap-2"
                >
                  <UserRound className="h-4 w-4 text-muted-foreground" />
                  <span>{myselfLabel}</span>
                  <Check
                    className={cn(
                      'ml-auto h-4 w-4',
                      selectedPersonId === null ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                </CommandItem>

                {/* Permitted people */}
                {permittedPeople.map(person => (
                  <CommandItem
                    key={person.id}
                    value={person.id}
                    keywords={[person.name]}
                    onSelect={() => handleSelect(person.id)}
                    className="gap-2"
                  >
                    <UserRound className="h-4 w-4 text-muted-foreground" />
                    <span>{person.name}</span>
                    <Check
                      className={cn(
                        'ml-auto h-4 w-4',
                        selectedPersonId === person.id ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
